import React, { Fragment } from "react"
import { TModuleFooter } from "@/types/base"
import Image from "next/image"
import Link from "next/link"
import Twitter from "../../../../public/Twitter.svg"
import Facebook from "../../../../public//Facebook.svg"
import Instagram from "../../../../public/Instagram.svg"
import Linkedin from "../../../../public/linkedin.svg"
import { Typography } from "@/components/ui/typography"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"

interface IFooter {
  data: TModuleFooter
}

const Footer = ({ data }: IFooter) => {
  const socialItems = [
    { iconRef: Facebook, name: "facebook" },
    { iconRef: Instagram, name: "instagram" },
    { iconRef: Twitter, name: "twitter" },
    { iconRef: Linkedin, name: "linkedIn", itemWidth: 32 },
  ]
  return (
    <Fragment>
      <hr
        key="hr"
        className="w-full order-[0] border-t border-black col-span-12 my-16"
      />
      <div className="flex flex-col justify-center w-full gap-10 pt-0 lg:module-wrapper-columns lg:mt-0 lg:pb-0 lg:mb-0 lg:gap-0">
        <div className="lg:col-span-6">
          <div className="flex flex-col items-start gap-1.5">
            <div className="flex items-center gap-2">
              <Image
                src={data?.footerLogo || ""}
                alt={data?.title || ""}
                height={30}
                width={28.8}
                className="dark:invert"
              />
              <p className="text-base font-extrabold uppercase">
                {data?.title}
              </p>
            </div>
            <Typography
              variant="body"
              className="text-xs max-w-80 font-normal leading-5 text-muted-foreground"
              as="div"
            >
              {documentToReactComponents(data?.contactUsText, options)}
            </Typography>
          </div>
        </div>
        <div className="lg:col-start-7 flex flex-col gap-5 lg:col-span-full lg:mb-24">
          {data?.appLinks?.map((item) => {
            const { id, label, url } = item
            return (
              <Link
                key={`footerLink_${id}`}
                href={url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl font-bold leading-relaxed"
              >
                {label}
              </Link>
            )
          })}
        </div>
        <div className="flex items-center gap-x-8 lg:col-start-7 lg:col-span-full lg:mb-6">
          {socialItems.map(({ name, iconRef, itemWidth = 32 }) => {
            const url = data?.socials[name]
            if (!url) return null
            return (
              <Link
                href={url}
                key={`social_${name}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src={iconRef}
                  alt={name}
                  width={itemWidth}
                  height={32}
                  className="dark:invert"
                />
              </Link>
            )
          })}
        </div>
        <div className="flex flex-col lg:row-start-2 col-start-1 lg:col-span-6 ">
          <div className="uppercase text-xl font-bold leading-relaxed">
            {data?.address1}
          </div>
          <div className="text-xs font-normal leading-relaxed">
            {data?.address2}
          </div>
        </div>
        <div className="lg:row-start-4 col-start-7 lg:col-start-7 lg:col-span-6 lg:mt-6">
          <div
            className="flex flex-col text-xs font-normal leading-relaxed
             uppercase gap-6 lg:flex-row lg:flex-wrap "
          >
            {data?.pageLinks?.map(({ id, label, url }) =>
              url ? (
                <a
                  key={`footerLink_${id}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline md:mb-0 cursor-pointer"
                >
                  {label}
                </a>
              ) : (
                <div key={`footerLink_${id}`} className=" md:mb-0">
                  {label}
                </div>
              ),
            )}
          </div>
        </div>
        <div className="col-start-7 lg:row-start-4 lg:col-span-6 lg:mt-6">
          <div className="uppercase text-xs font-normal leading-relaxed">
            {data?.copyright}
          </div>
        </div>
      </div>
    </Fragment>
  )
}

export default Footer

const options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [INLINES.HYPERLINK]: (node, children) => {
      const url = node.data.uri
      return (
        <a
          href={url}
          className="underline hover:text-foreground transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      )
    },
  },
}
